import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import withStyles from '@material-ui/core/styles/withStyles';
import { Typography } from '@material-ui/core';
import classNames from 'classnames';
import { isEmpty, isArray } from 'lodash';
import { injectIntl } from 'react-intl';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import { numberMaskWithoutDecimalOption } from 'aurora-utils/constants';
import {
  ADGMPanel,
  ADGMGridLayoutV2,
  ADGMTextField,
  ADGMButton,
  ADGMImage,
  PanelHeaderActionButtons,
} from 'aurora-components';
import { DocumentsV2, AddRepresentative } from 'aurora-section-components';
import { keyEmptyCheck } from 'aurora-utils/utils';
import messages from 'aurora-translations';

import useBuyerDetailsActions from '../helpers';
import { BUYER_TYPES, BUYER_DOCUMENTS_ATTRIBUTES } from '../constants';

import styles from './styles';

const BuyersSummary = ({
  intl,
  classes,
  locale,
  application,
  buyers,
  documents,
  error,
  addBuyerHandler,
  editBuyerHandler,
  deleteBuyerHandler,
  addRepresentativeHandler,
  deleteRepresentativeHandler,
  buyerTransactionShareChangeHandler,
  downloadDocumentHandler,
}) => {
  if (!isArray(buyers) || isEmpty(buyers)) return null;

  const {
    getBuyerType,
    transformDetails,
    getBuyerDocuments,
    getBuyerRepresentativeDocuments,
  } = useBuyerDetailsActions();

  const getBuyerGridLayoutDetails = useCallback(
    (buyer, buyerType) => {
      return [
        {
          label: 'Name',
          value: locale === 'en' ? buyer?.nameEn || buyer?.nameAr : buyer?.nameAr || buyer?.nameEn,
          isPersonName: true,
        },
        ...(buyerType === BUYER_TYPES?.UAE_RESIDENT
          ? [
              {
                label: 'emirates_ID',
                value: buyer?.eid,
              },
              {
                label: 'nationality',
                value:
                  locale === 'en'
                    ? buyer?.nationalityEn || buyer?.nationalityAr
                    : buyer?.nationalityAr || buyer?.nationalityEn,
              },
            ]
          : []),
        ...(buyerType === BUYER_TYPES?.COMPANY || buyerType === BUYER_TYPES?.GOVERNMENT
          ? [
              {
                label: 'trade_license_number',
                value: buyer?.tradeLicense || buyer?.companyTradeLicenseNumber,
              },
            ]
          : []),
        {
          label: 'Phone_Number',
          value: <bdi>{buyer?.phoneNumber ?? '-'}</bdi>,
        },
        {
          label: 'email',
          value: buyer?.email,
        },
      ];
    },
    [locale],
  );

  const renderBuyer = (buyer, buyerListingNo) => {
    const buyerType = getBuyerType(buyer);
    if (!buyerType) return null;
    const transformBuyerDetails = transformDetails(buyer, buyerType);
    const buyerDocuments = getBuyerDocuments(buyer, buyerType, documents);
    const buyerRepresentativeDocs = getBuyerRepresentativeDocuments(buyer, buyerType, documents);

    return (
      <ADGMPanel
        title={`#${buyerListingNo} ${intl.formatMessage({
          ...messages.OFFPLAN_ONLINE_BUYER_DETAILS,
        })}`}
        customClasses={{
          container: classNames(classes.panel, {
            [classes.firstPanel]: buyerListingNo === 1,
          }),
          titleHead: classes.panelTitleHead,
          title: classes.panelTitle,
        }}
        extraHeaderComponents={
          <PanelHeaderActionButtons
            deleteHandler={() => deleteBuyerHandler(buyer)}
            {...((buyerType === BUYER_TYPES.UAE_RESIDENT || buyerType === BUYER_TYPES.COMPANY) && {
              editHandler: () => editBuyerHandler(buyer),
            })}
          />
        }
      >
        <div className={classes.panelContent}>
          <ADGMGridLayoutV2
            gridData={getBuyerGridLayoutDetails(transformBuyerDetails, buyerType)}
            withIcon
            customClasses={{ container: classes.containerPadding }}
          />

          <div className={classNames(classes.buyingSharesHolder, classes.containerPadding)}>
            <Typography className={classes.buyingPercentageTitle}>{keyEmptyCheck('buying_percentage')}</Typography>
            <div className={classes.buyingPercentageHolder}>
              <ADGMTextField
                label={keyEmptyCheck('buying_percentage')}
                value={buyer?.transactionShare || ''}
                onChange={e => buyerTransactionShareChangeHandler(buyer, +e?.target?.value)}
                endAdornment={<span className={classes.percentageSymbol}>%</span>}
                mask={createNumberMask(numberMaskWithoutDecimalOption)}
                required
              />
              <Typography className={classes.totalBuyingPercentageMessage}>
                {keyEmptyCheck('out_of_total_sold_percentage', {
                  s: (
                    <span className={classes.totalBuyingPercentage}>
                      <bdi>100%</bdi>
                    </span>
                  ),
                })}
              </Typography>
            </div>
          </div>
          {!isEmpty(buyerDocuments) && (
            <div className={classNames(classes.documentsHolder, classes.containerPadding)}>
              <DocumentsV2
                title='documents'
                isPanel={false}
                isEditable={false}
                hideDownloadAllBtn
                handleDocDownload={downloadDocumentHandler}
                {...{
                  lang: locale,
                  applicationId: application?.applicationId,
                  applicationType: application?.applicationType,
                  documents: buyerDocuments,
                  attributes: BUYER_DOCUMENTS_ATTRIBUTES[buyerType],
                }}
              />
            </div>
          )}
          <div className={classNames(classes.addRepresentativeHolder)}>
            <AddRepresentative
              isEditable
              application={application}
              party={buyer}
              representative={buyer?.representative}
              isPartyTypeCompany={buyerType === BUYER_TYPES.COMPANY || buyerType === BUYER_TYPES.GOVERNMENT}
              documents={buyerRepresentativeDocs}
              mandatory={buyer?.underage || buyerType === BUYER_TYPES.COMPANY || buyerType === BUYER_TYPES.GOVERNMENT}
              alertText={
                buyer?.underage
                  ? 'under_age_buyer_info'
                  : buyerType === BUYER_TYPES.COMPANY || buyerType === BUYER_TYPES.GOVERNMENT
                  ? 'company_representative_info'
                  : 'individual_representative_info_for_buyer'
              }
              addForAllPartiesText='add_this_representative_for_other_buyers'
              disableAddBtn={!isEmpty(buyer?.representative)}
              error={error}
              addRepresentativeHandler={addRepresentativeHandler}
              deleteRepresentativeHandler={deleteRepresentativeHandler}
              docsAttributes={[
                {
                  active: true,
                  attributeType: 'file',
                  attributeSubType: 'file',
                  name: 'POA_DOC',
                  description: null,
                  title: 'POWER_OF_ATTORNEY',
                  isMultiple: true,
                  mandatory: true,
                  acceptFileTypes: 'application/pdf',
                  ...(buyerType === BUYER_TYPES.UAE_RESIDENT && { userId: buyer?.userId }),
                  ...((buyerType === BUYER_TYPES.COMPANY || buyerType === BUYER_TYPES.GOVERNMENT) && {
                    companyId: buyer?.companyId,
                  }),
                },
              ]}
            />
          </div>
        </div>
      </ADGMPanel>
    );
  };

  return (
    <>
      <div className={classes.container}>
        {buyers?.map((buyer, i) => renderBuyer(buyer, i + 1))}
        <div className={classes.addBuyerHolder}>
          <ADGMButton variant='secondary' className={classes.addBuyerBtn} onClick={addBuyerHandler}>
            <ADGMImage name='add-icon2.svg' alt='add-buyer' />
            {keyEmptyCheck('add_new_buyer')}
          </ADGMButton>
        </div>
      </div>
    </>
  );
};

BuyersSummary.propTypes = {
  intl: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  locale: PropTypes.string.isRequired,
  application: PropTypes.object.isRequired,
  buyers: PropTypes.array.isRequired,
  documents: PropTypes.array.isRequired,
  error: PropTypes.any.isRequired,
  addBuyerHandler: PropTypes.func.isRequired,
  editBuyerHandler: PropTypes.func.isRequired,
  deleteBuyerHandler: PropTypes.func.isRequired,
  addRepresentativeHandler: PropTypes.func.isRequired,
  deleteRepresentativeHandler: PropTypes.func.isRequired,
  buyerTransactionShareChangeHandler: PropTypes.func.isRequired,
  downloadDocumentHandler: PropTypes.func.isRequired,
};

BuyersSummary.defaultProps = {};

export default withStyles(styles)(injectIntl(BuyersSummary));
